import { useCallback, useEffect, useState } from "react";

export type WatchlistAlertMode = "off" | "highlight" | "filter" | "both";

const STORAGE_KEY = "watchlist.alert.mode";
const DEFAULT_MODE: WatchlistAlertMode = "off";

/**
 * Hook to manage watchlist alert mode setting via localStorage.
 * This setting controls when audio alerts are triggered for watchlist matches.
 */
export function useWatchlistAlertMode() {
  const [mode, setModeState] = useState<WatchlistAlertMode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && ["off", "highlight", "filter", "both"].includes(stored)) {
        return stored as WatchlistAlertMode;
      }
    } catch {
      // localStorage not available
    }
    return DEFAULT_MODE;
  });

  // Sync to localStorage when mode changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // localStorage not available
    }
  }, [mode]);

  // Listen for changes in other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        if (["off", "highlight", "filter", "both"].includes(e.newValue)) {
          setModeState(e.newValue as WatchlistAlertMode);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const setMode = useCallback((newMode: WatchlistAlertMode) => {
    setModeState(newMode);
  }, []);

  return { watchlistAlertMode: mode, setWatchlistAlertMode: setMode };
}
